import { isNil } from 'lodash-es'
import type { CustomFieldType } from '@/services/domain/cusomFields/types'
import type { PaginationUrlFilterScalarValueType, PaginationUrlFilterType } from '@/services/tables/types'
import type { CustomFieldPaginationUrlFilterScalarValueType } from '@/utils/custom-fields/types'
import type { Nullable, Optional } from '@/types'
import { getCustomFieldSchemaService } from '@/core/container/ioc'
import {
  contactNotesFilterNamePrefix,
  expandContactNotesFilters,
  parseContactNotesFilterName,
} from '@/utils/contacts/contactNotesFilter'

export const CUSTOM_FIELD_DATE_VALUE = 'dateValue' as const
export const CUSTOM_FIELD_TEXT_VALUE = 'textValue' as const
export const CUSTOM_FIELD_NUMERIC_VALUE = 'numericValue' as const
export const CUSTOM_FIELD_MONETARY_VALUE = 'monetaryValue' as const
export const CUSTOM_FIELD_SINGLE_VALUE = 'singleValue' as const
export const CUSTOM_FIELD_MULTI_VALUE = 'multiValue' as const

export const CUSTOM_FIELD_SUPPORTED_VALUES = [
  CUSTOM_FIELD_DATE_VALUE,
  CUSTOM_FIELD_TEXT_VALUE,
  CUSTOM_FIELD_NUMERIC_VALUE,
  CUSTOM_FIELD_MONETARY_VALUE,
  CUSTOM_FIELD_SINGLE_VALUE,
  CUSTOM_FIELD_MULTI_VALUE,
] as const

export type CustomFieldSupportedValues = (typeof CUSTOM_FIELD_SUPPORTED_VALUES)[number]

export const customFieldFilterValueKeyMap: Record<CustomFieldType, CustomFieldSupportedValues> = {
  date: CUSTOM_FIELD_DATE_VALUE,
  dateTime: CUSTOM_FIELD_DATE_VALUE,
  text: CUSTOM_FIELD_TEXT_VALUE,
  numeric: CUSTOM_FIELD_NUMERIC_VALUE,
  monetary: CUSTOM_FIELD_MONETARY_VALUE,
  singleOption: CUSTOM_FIELD_SINGLE_VALUE,
  multiOption: CUSTOM_FIELD_MULTI_VALUE,
}

export const getCustomFieldTypeByValueType = (valueType: CustomFieldSupportedValues): CustomFieldType => {
  const entry = Object.entries(customFieldFilterValueKeyMap).find(([, value]) => value === valueType)
  return (entry?.[0] ?? 'text') as CustomFieldType
}

export const customFieldFilterNamePrefix = 'customFields'
const customFieldFilterNameSeparator = ':'

export type CustomFieldFilterName<
  Type extends CustomFieldType = CustomFieldType,
  Id extends string = string,
> = `${typeof customFieldFilterNamePrefix}:${Type}:${Id}`

export const buildCustomFieldFilterName = <Id extends string = string, Type extends CustomFieldType = CustomFieldType>(
  customFieldId: Id,
  customFieldType: Type,
) => {
  return [customFieldFilterNamePrefix, customFieldType, customFieldId].join(
    customFieldFilterNameSeparator,
  ) as CustomFieldFilterName<Type, Id>
}

export const parseCustomFieldFilterName = (filterName: string) => {
  const filterNameParts = filterName.split(customFieldFilterNameSeparator)

  if (filterNameParts.length < 3) {
    return null
  }

  const [prefix, customFieldType, customFieldId] = filterNameParts

  if (prefix !== customFieldFilterNamePrefix) {
    return null
  }

  return {
    prefix,
    customFieldId,
    customFieldType: customFieldType as CustomFieldType,
  } as const
}

export const isCustomFieldName = (fieldName: string) => {
  return fieldName.startsWith(customFieldFilterNamePrefix + customFieldFilterNameSeparator)
}

export const prepareCustomFieldFilters = (filters: Optional<Nullable<PaginationUrlFilterType>>) => {
  if (!filters) {
    return []
  }
  return filters.map((filter) => {
    return Object.entries(filter).reduce((acc, [key, value]) => {
      const contactNotesFilterField = parseContactNotesFilterName(key)

      if (contactNotesFilterField) {
        acc[contactNotesFilterNamePrefix] = { ...acc[contactNotesFilterNamePrefix], [contactNotesFilterField]: value }
        return acc
      }

      const parsedCustomFieldFilterName = parseCustomFieldFilterName(key)

      if (!parsedCustomFieldFilterName) {
        acc[key] = value
        return acc
      }

      if (!acc[parsedCustomFieldFilterName.prefix]) {
        acc[parsedCustomFieldFilterName.prefix] = []
      }

      acc[parsedCustomFieldFilterName.prefix].push(
        buildCustomFieldFilter(
          parsedCustomFieldFilterName.customFieldId,
          parsedCustomFieldFilterName.customFieldType,
          value,
        ),
      )

      return acc
    }, {})
  }, [])
}

/**
 * Opposite to {@link prepareCustomFieldFilters}
 */
const resolveCustomFieldTypeFromSchema = (id: string, fallbackType: CustomFieldType): CustomFieldType => {
  return getCustomFieldSchemaService().getCustomField(id)?.fieldType ?? fallbackType
}

export const parsePaginationUrlFilterType = (
  filters: PaginationUrlFilterType,
  resolveCustomFieldType: (
    id: string,
    fallbackType: CustomFieldType,
  ) => CustomFieldType = resolveCustomFieldTypeFromSchema,
): PaginationUrlFilterType => {
  return filters.map((filterLine) => {
    const result = { ...filterLine }

    if (customFieldFilterNamePrefix in filterLine) {
      delete result[customFieldFilterNamePrefix]

      const customFields = Object.values(
        filterLine[customFieldFilterNamePrefix],
      ) as unknown as CustomFieldPaginationUrlFilterScalarValueType[]

      customFields.forEach((customField) => {
        const customFieldValueType = Object.keys(customField).find((key) => {
          return (CUSTOM_FIELD_SUPPORTED_VALUES as readonly string[]).includes(key) && !isNil(customField[key])
        }) as CustomFieldSupportedValues

        const fallbackType = getCustomFieldTypeByValueType(customFieldValueType)
        const fieldType = resolveCustomFieldType ? resolveCustomFieldType(customField.id, fallbackType) : fallbackType

        result[buildCustomFieldFilterName(customField.id, fieldType)] = customField[
          customFieldValueType
        ] as PaginationUrlFilterScalarValueType
      })
    }

    return expandContactNotesFilters(result)
  })
}

export const buildCustomFieldFilter = (
  customFieldId: string,
  customFieldType: CustomFieldType,
  value: PaginationUrlFilterScalarValueType,
): CustomFieldPaginationUrlFilterScalarValueType => {
  return {
    id: customFieldId,
    [customFieldFilterValueKeyMap[customFieldType]]: value,
  }
}
